import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Plus } from 'lucide-react';

const SHIMMER_FLAG = 'frootex_fab_shimmered';

/**
 * Floating action button for creating a new post.
 * - Fixed bottom-right, floats above BottomNav.
 * - Runs a single shimmer sweep once per browser session (first app visit).
 */
export default function CreatePostFAB() {
  const navigate = useNavigate();
  const location = useLocation();
  const [shimmer, setShimmer] = useState(false);

  useEffect(() => {
    try {
      if (!sessionStorage.getItem(SHIMMER_FLAG)) {
        setShimmer(true);
        sessionStorage.setItem(SHIMMER_FLAG, '1');
        // Cleanly remove the class after the animation completes so it can't retrigger on style recalcs.
        const t = setTimeout(() => setShimmer(false), 1800);
        return () => clearTimeout(t);
      }
    } catch {
      // sessionStorage disabled — silently skip.
    }
  }, []);

  // Hide on the create-post page itself so it doesn't overlap the form.
  if (location.pathname === '/create-post') return null;

  return (
    <button
      type="button"
      data-testid="fab-create-post"
      aria-label="Create post"
      onClick={() => navigate('/create-post')}
      className={`fab-create fixed right-5 z-[10000] w-14 h-14 rounded-full overflow-hidden
                  flex items-center justify-center
                  bg-gradient-to-br from-[#0E5D4A] via-[#0B4F3F] to-[#083D31]
                  text-[#F6F8FB]
                  shadow-[0_18px_44px_-14px_rgba(11,79,63,0.75),0_6px_18px_-8px_rgba(11,22,32,0.35),inset_0_1px_0_rgba(255,255,255,0.16)]
                  ring-1 ring-white/10
                  transition-transform duration-200
                  hover:-translate-y-0.5 active:scale-90
                  ${shimmer ? 'fab-shimmer-once' : ''}`}
      style={{ bottom: 'calc(env(safe-area-inset-bottom) + 88px)' }}
    >
      {/* Ambient breathing halo */}
      <span aria-hidden className="pointer-events-none absolute -inset-1 rounded-full bg-[#0B4F3F]/25 blur-xl opacity-70" />
      {/* Inner subtle top gloss for depth */}
      <span aria-hidden className="pointer-events-none absolute inset-x-2 top-1 h-3 rounded-full bg-white/15 blur-[6px]" />
      <Plus className="relative w-6 h-6" strokeWidth={2.6} />
    </button>
  );
}
